import { useMemo } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { SystemParameterInterface } from 'ks-common';
import { FormContainer } from 'app/hooks/useGlobalForm';
import {
  DayBounds,
  IsoDate,
  RENTAL_COPY,
  changeDelivery,
  deliveryBounds,
  isBlocked,
  returnBounds,
  todayIso,
} from 'utils/rentalDates';

export type DateFieldType = 'delivery' | 'return';

export interface DateFieldModel {
  label: 'Delivery Date' | 'Return Date';
  value: IsoDate | null;
  bounds: DayBounds | null;
  openTo?: IsoDate;
  isBlocked: (day: IsoDate) => boolean;
  helper?: { text: string; tone: 'info' | 'warning' };
  error: boolean;
  select: (day: IsoDate | null) => void;
}

export function useDateField(type: DateFieldType): DateFieldModel {
  const {
    currentProduct,
    updateCurrentProduct,
    validations,
    returnClearedFor,
    setReturnClearedFor,
  } = FormContainer.useContainer();
  const [systemParams] =
    useLocalStorageState<SystemParameterInterface[]>('systemParams');

  const blockedDates = useMemo(
    () =>
      systemParams?.find(param => param.ParamName === 'Blocked_Dates')
        ?.ParamValue,
    [systemParams],
  );

  const deliveryDate = currentProduct?.deliveryDate ?? null;
  const returnDate = currentProduct?.returnDate ?? null;

  const isBlockedDay = (day: IsoDate) => isBlocked(blockedDates, day);

  if (type === 'delivery') {
    const showClearedNotice =
      returnClearedFor !== null &&
      returnClearedFor === deliveryDate &&
      returnDate === null;

    return {
      label: 'Delivery Date',
      value: deliveryDate,
      bounds: deliveryBounds(todayIso()),
      isBlocked: isBlockedDay,
      helper: showClearedNotice
        ? { text: RENTAL_COPY.returnCleared, tone: 'warning' }
        : undefined,
      error: !!validations.deliveryDateIsToday,
      select: day => {
        const { patch, returnCleared } = changeDelivery(
          { deliveryDate, returnDate },
          day,
        );
        updateCurrentProduct(patch);
        setReturnClearedFor(returnCleared ? day : null);
      },
    };
  }

  const bounds = deliveryDate ? returnBounds(deliveryDate) : null;

  return {
    label: 'Return Date',
    value: returnDate,
    bounds,
    openTo: deliveryDate ?? undefined,
    isBlocked: isBlockedDay,
    helper: {
      text: bounds
        ? RENTAL_COPY.maxPeriod
        : `${RENTAL_COPY.deliveryFirst} ${RENTAL_COPY.maxPeriod}`,
      tone: 'info',
    },
    error: false,
    select: day => {
      updateCurrentProduct({ returnDate: day });
      setReturnClearedFor(null);
    },
  };
}
